<script setup lang="ts">
import type { Placement } from '@popperjs/core'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/tooltip'

const props = withDefaults(
  defineProps<{
    text?: string | null
    tooltipPosition?: Placement
    width?: string
  }>(),
  {
    text: null,
    tooltipPosition: 'top',
    width: undefined
  }
)

const emit = defineEmits<{
  (e: 'opened'): void
  (e: 'closed'): void
}>()

const slots = useSlots()

const isTooltipActive = computed(() => Boolean(props.text) || Boolean(slots['tooltip-content']))

const side = computed(() => {
  const base = props.tooltipPosition.split('-')[0]
  return (['top', 'bottom', 'left', 'right'] as const).includes(base as never)
    ? (base as 'top' | 'bottom' | 'left' | 'right')
    : 'top'
})

const align = computed(() => {
  const suffix = props.tooltipPosition.split('-')[1]
  return suffix === 'start' ? 'start' : suffix === 'end' ? 'end' : 'center'
})

function onOpenChange(open: boolean) {
  emit(open ? 'opened' : 'closed')
}
</script>

<template>
  <Tooltip :disabled="!isTooltipActive" @update:open="onOpenChange">
    <TooltipTrigger as-child>
      <slot />
    </TooltipTrigger>
    <TooltipContent :side="side" :align="align" :class="width">
      <span v-if="text">{{ text }}</span>
      <slot v-else name="tooltip-content" />
    </TooltipContent>
  </Tooltip>
</template>
